import type { MinimalTokenInfo, TokenInfo } from "@stepfinance/common";
import { NATIVE_SOL } from "@stepfinance/common";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import { useEffect, useMemo, useState } from "react";

import { useAccountsContext } from "../../contexts/accounts";
import { useNetworkConfig, useUnknownTokenConfig } from "../../contexts/app/config";
import { getTokenMap, getTokenName, isKnownMint } from "../../utils/tokenInfo";

type UserBalancesMap = {
  [mint: string]: NormalizedTokenAccount;
};

export const useSwappableTokens = () => {
  const tokenMap = getTokenMap();
  const { network } = useNetworkConfig();
  const { hideUnknownTokens } = useUnknownTokenConfig();
  const { userTokenAccounts } = useAccountsContext();
  const [tokenList, setTokenList] = useState<MinimalTokenInfo[]>([]);
  const [tokenListMap, setTokenListMap] = useState<Map<string, TokenInfo>>(new Map());
  const userBalancesMap = useMemo(
    () =>
      userTokenAccounts.reduce<UserBalancesMap>((map, value) => {
        return { ...map, [value.info.mint]: value };
      }, {}),
    [userTokenAccounts],
  );

  useEffect(() => {
    const foundTokenAddresses = new Set<string>();
    const userTokens: MinimalTokenInfo[] = [];
    const unknownUserTokens: MinimalTokenInfo[] = [];
    const otherTokens: MinimalTokenInfo[] = [];
    for (const token of Object.values(tokenMap)) {
      const notAlreadyFoundSPLToken = !foundTokenAddresses.has(token.address);
      const isSwappableSPLToken =
        notAlreadyFoundSPLToken &&
        !token.tags?.includes("nft") &&
        isKnownMint(token.address, hideUnknownTokens) &&
        token.address !== NATIVE_SOL.toBase58();
      if (isSwappableSPLToken) {
        if (userBalancesMap[token.address]) {
          userTokens.push(token);
        } else {
          otherTokens.push(token);
        }
      }
      foundTokenAddresses.add(token.address);
    }

    for (const [mintAddress, tokenBalanceInfo] of Object.entries(userBalancesMap)) {
      if (!foundTokenAddresses.has(mintAddress)) {
        unknownUserTokens.push({
          address: tokenBalanceInfo.info.mint,
          chainId: 101,
          decimals: tokenBalanceInfo.tokenAmount.decimals,
          name: getTokenName(mintAddress),
          symbol: getTokenName(mintAddress),
        });
        foundTokenAddresses.add(tokenBalanceInfo.info.mint);
      }
    }

    const sortedUserTokens = userTokens.sort((a, b) =>
      (userBalancesMap[a.address].tokenAmount.balanceInUSD ?? 0) <
      (userBalancesMap[b.address].tokenAmount.balanceInUSD ?? 0)
        ? 1
        : -1,
    );
    const sortedUnknownUserTokens = unknownUserTokens.sort((a, b) =>
      userBalancesMap[a.address].tokenAmount.uiAmount < userBalancesMap[b.address].tokenAmount.uiAmount ? 1 : -1,
    );
    const updatedTokenList = [...sortedUserTokens, ...sortedUnknownUserTokens, ...otherTokens];
    setTokenList(updatedTokenList);

    const swappableTokenMapEntries = updatedTokenList.map((tokenInfo) => {
      return [tokenInfo.address, tokenInfo] as const;
    });
    setTokenListMap(new Map(swappableTokenMapEntries));
  }, [userBalancesMap, tokenMap, network, hideUnknownTokens]);

  return {
    swappableTokens: tokenList,
    swappableTokensMap: tokenListMap,
  };
};
